<template>
  <v-container class="app-container--width-max">
    <v-card class="rounded-lg elevation-3 pa-4 pa-xl-16 my-5 mx-3">
      <v-row>
        <v-col>
          <div class="d-flex">
            <v-icon color="accent" large>mdi-file-find</v-icon>&nbsp;
            <h2 class="font-weight-bold">Fact find</h2>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <ValidationObserver v-slot="{ passes }">
            <form @submit.prevent="passes(onSubmit)">
              <v-row>
                <v-col>
                  <h3 class="font-20 mb-3">Current insurance arrangements</h3>
                </v-col>
              </v-row>
              <v-row class="mt-n4">
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Do you currently have an insurance policy in place?
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.existingInsurancePolicy"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <v-row
                v-if="form.existingInsurancePolicy"
                style="margin-bottom: -26px"
              >
                <v-col class="pb-0">
                  <v-row>
                    <v-col>
                      <label class="mb-0 font-weight-medium font-18">
                        When is your policy due to be renewed?
                      </label>
                      <BaseInput
                        class="input-md"
                        :dense="$vuetify.breakpoint.lgAndUp"
                        placeholder="DD/MM/YYYY"
                        v-mask="'##/##/####'"
                        v-model="form.renewalDate"
                        :rules="{
                          required: {
                            fieldLabel: 'Renewal date',
                          },
                          regex: {
                            pattern: '^\\s*$|^(\\d{2})\\/(\\d{2})\\/\\d{4}$',
                            fieldLabel: 'Renewal date',
                          },
                          date_valid: {
                            fieldLabel: 'Renewal date',
                          },
                          date_in_range: {
                            max: 1,
                            units: 'years',
                            fieldLabel: 'Renewal date',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-0">
                    <v-col class="pt-0">
                      <label class="mb-0 font-weight-medium font-18">
                        Who are you currently insured with?
                      </label>
                      <BaseInput
                        class="input-md"
                        :dense="$vuetify.breakpoint.lgAndUp"
                        v-model="form.insuranceProvider"
                        :rules="{
                          required: {
                            fieldLabel: 'Insurance provider',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-0 mb-3">
                    <v-col class="pt-0">
                      <BaseRadio
                        v-model="form.coverType"
                        label="What cover do you currently have?"
                        labelClass="font-18 font-weight-medium"
                        pslStyle="image"
                        :items="coverTypeOptions"
                        :rules="{
                          required: {
                            required: true,
                            fieldLabel: 'Insurance provider',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="isCoverTypeQuestionBuildings" class="mt-0">
                    <v-col class="pt-0">
                      <label class="mb-0 font-weight-medium font-18">
                        Current buildings cover amount?
                      </label>
                      <BaseInput
                        class="input-sm"
                        :dense="$vuetify.breakpoint.lgAndUp"
                        prefix="£"
                        v-model="form.buildingsAmount"
                        :rules="{
                          required: {
                            fieldLabel: 'Buildings amount',
                          },
                          regex: {
                            pattern: '^[0-9][0-9.,]*$',
                            fieldLabel: 'Buildings amount',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="isCoverTypeQuestionContents" class="mt-0">
                    <v-col class="pt-0">
                      <label class="mb-0 font-weight-medium font-18">
                        Current contents cover amount?
                      </label>
                      <BaseInput
                        class="input-sm"
                        :dense="$vuetify.breakpoint.lgAndUp"
                        prefix="£"
                        v-model="form.contentsAmount"
                        :rules="{
                          required: {
                            fieldLabel: 'Contents amount',
                          },
                          regex: {
                            pattern: '^[0-9][0-9.,]*$',
                            fieldLabel: 'Contents amount',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-0">
                    <v-col class="pt-0">
                      <label class="mb-0 font-weight-medium font-18">
                        Payment Frequency
                      </label>
                      <BaseToggleButton
                        style="max-width: 417px"
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                        :toggleButtonText="paymentFrequencyOptions"
                        v-model="form.currentPaymentFrequency"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <label class="mb-0 font-weight-medium font-18">
                        Current premium?
                      </label>
                      <BaseInput
                        class="input-sm"
                        :dense="$vuetify.breakpoint.lgAndUp"
                        prefix="£"
                        v-model="form.currentPremium"
                        :rules="{
                          required: {
                            fieldLabel: 'Current premium',
                          },
                          regex: {
                            pattern: '^[0-9][0-9.,]*$',
                            fieldLabel: 'Current premium',
                          },
                        }"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row class="mt-9">
                <v-col>
                  <h3 class="font-20 mb-3">Demands and needs</h3>
                </v-col>
              </v-row>
              <v-row class="mt-n4">
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Is it important that you have cover for your buildings?
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.buildingsCover"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <!-- start of hidden question -->
              <v-row v-if="form.buildingsCover">
                <v-col>
                  <v-row>
                    <v-col>
                      <label class="mb-0 font-weight-medium font-18">
                        Is it important that you have accidental damage cover
                        for your buildings?
                      </label>
                      <BaseToggleButton
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                        v-model="form.buildingsAccidentalCover"
                        style="max-width: 276px"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <!-- end of hidden question -->
              <v-row>
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Is it important that you have cover for your contents?
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.contentsCover"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <!-- start of hidden question -->
              <v-row v-if="form.contentsCover">
                <v-col>
                  <v-row>
                    <v-col>
                      <label class="mb-0 font-weight-medium font-18">
                        Is it important that you have accidental damage cover
                        for your contents?
                      </label>
                      <BaseToggleButton
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                        v-model="form.contentsAccidentalCover"
                        style="max-width: 276px"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <label class="mb-0 font-weight-medium font-18">
                        Is it important that you have cover for your personal
                        belongings away from home?
                      </label>
                      <BaseToggleButton
                        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                        v-model="form.coverPersonalBelongings"
                        style="max-width: 276px"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <!-- end of hidden question -->
              <v-row>
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Would you like cover for home emergencies
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.coverHomeEmergencies"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Would you like cover for legal expenses
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.coverLegalExpenses"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Is it important that you are able to choose the level of
                    your policy excess?
                  </label>
                  <BaseToggleButton
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    v-model="form.policyExcess"
                    style="max-width: 276px"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Would you prefer to pay monthly or annually?
                  </label>
                  <BaseToggleButton
                    style="max-width: 417px"
                    :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
                    :toggleButtonText="paymentFrequencyOptions"
                    v-model="form.newPaymentFrequency"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-9">
                <v-col>
                  <h3 class="font-20 mb-3">
                    Household income &amp; expenditure
                  </h3>
                </v-col>
              </v-row>
              <v-row class="mt-n4">
                <v-col>
                  <label class="mb-0 font-weight-medium font-18">
                    Monthly net income
                  </label>
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.monthlyNetIncome"
                    :rules="{
                      required: {
                        fieldLabel: 'Monthly net income',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Monthly net income',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Monthly net income',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18"
                    >Mortgage &amp; home</label
                  >
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.mortgageAndHome"
                    :rules="{
                      required: {
                        fieldLabel: 'Mortgage & home',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Mortgage & home',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Mortgage & home',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18"
                    >Lifestyle</label
                  >
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.lifestyle"
                    :rules="{
                      required: {
                        fieldLabel: 'Lifestyle',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Lifestyle',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Lifestyle',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18">Travel</label>
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.travel"
                    :rules="{
                      required: {
                        fieldLabel: 'Travel',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Travel',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Travel',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18">
                    Borrowing &amp; debts
                  </label>
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.borrowingAndDebts"
                    :rules="{
                      required: {
                        fieldLabel: 'Borrowing & debts',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Borrowing & debts',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Borrowing & debts',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18"
                    >Day to day expenses</label
                  >
                  <BaseInput
                    class="input-sm"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    v-model="form.expenses"
                    :rules="{
                      required: {
                        fieldLabel: 'Day to day expenses',
                      },
                      regex: {
                        pattern: '^[0-9][0-9,]*$',
                        fieldLabel: 'Day to day expenses',
                      },
                      max: {
                        length: 8,
                        fieldLabel: 'Day to day expenses',
                      },
                    }"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18">
                    Total monthly expenditure
                  </label>
                  <BaseInput
                    class="input-sm input-disabled"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    disabled
                    hideValidationIcon
                    :value="totalMonthlyExpenditure | formatDecimal"
                  />
                </v-col>
              </v-row>
              <v-row class="mt-0">
                <v-col class="pt-0">
                  <label class="mb-0 font-weight-medium font-18">
                    Spendable income
                  </label>
                  <BaseInput
                    class="input-sm input-disabled"
                    :dense="$vuetify.breakpoint.lgAndUp"
                    prefix="£"
                    disabled
                    hideValidationIcon
                    :value="totalSpendableIncome | formatDecimal"
                  />
                </v-col>
              </v-row>
              <v-row style="margin-top: 10px">
                <v-col>
                  <h3 class="font-20 mb-3">Specific needs</h3>
                </v-col>
              </v-row>
              <v-row class="mt-n4">
                <v-col>
                  <BaseTextarea
                    class="input-lg"
                    label="For the purposes of the quote, are there any specific insurance requirements you have?"
                    hideValidationIcon
                    v-model="form.specificNeeds"
                    :rules="{
                      max: {
                        length: 500,
                        fieldLabel: 'Specific needs',
                      },
                    }"
                  />
                  <BaseButton
                    depressed
                    height="60"
                    :width="$vuetify.breakpoint.lgAndUp ? '275px' : '100%'"
                    text="UPDATE FACT FIND"
                    rounded
                    :color="'psorange'"
                    class="font-weight-bold font-20"
                    type="submit"
                  />
                </v-col>
              </v-row>
            </form>
          </ValidationObserver>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import BaseTextarea from "@/components/BaseComponents/BaseTextarea";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import { mapState, mapActions } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { FACT_FIND_FORM } from "@/constants/factfind.constants";
import { populateFactFindFromApi } from "@/common/factfind";

export default {
  name: "FactFind",
  components: {
    ValidationObserver,
    BaseButton,
    BaseInput,
    BaseRadio,
    BaseTextarea,
    BaseToggleButton,
  },
  data() {
    return {
      paymentFrequencyOptions: {
        yes: "Monthly",
        no: "Annually",
      },
      coverTypeOptions: [
        {
          text: "Buildings &amp;<br>Contents",
          value: "BuildingsAndContents",
          dataUiid: "CoverType_BuildingsAndContents",
          image: {
            path: "ic_buildings_and_contents.svg",
            width: "90px",
            alt: "Buildings And Contents",
          },
        },
        {
          text: "Buildings<br>Only",
          value: "BuildingsOnly",
          dataUiid: "CoverType_BuildingsOnly",
          image: {
            path: "ic_buildings.svg",
            width: "60px",
            alt: "Buildings Only",
          },
        },
        {
          text: "Contents<br>Only",
          value: "ContentsOnly",
          dataUiid: "CoverType_ContentsOnly",
          image: {
            path: "ic_contents.svg",
            width: "70px",
            alt: "Contents Only",
          },
        },
      ],
      form: FACT_FIND_FORM,
    };
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["quoteResponse"]),
    totalMonthlyExpenditure() {
      const total =
        this.removeCharacters(this.form.mortgageAndHome) +
        this.removeCharacters(this.form.lifestyle) +
        this.removeCharacters(this.form.travel) +
        this.removeCharacters(this.form.borrowingAndDebts) +
        this.removeCharacters(this.form.expenses);
      return total.toString();
    },
    totalSpendableIncome() {
      return this.form.monthlyNetIncome - this.totalMonthlyExpenditure;
    },
    isCoverTypeQuestionBuildings() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "BuildingsOnly"
      );
    },
    isCoverTypeQuestionContents() {
      return (
        this.form.coverType === "BuildingsAndContents" ||
        this.form.coverType === "ContentsOnly"
      );
    },
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, ["submitFactFind"]),
    removeCharacters(value) {
      const removeCharacters = value.replaceAll(/[,]/g, "");
      const stringToNum = parseInt(removeCharacters);
      return value.length ? stringToNum : 0;
    },
    onSubmit() {
      this.submitFactFind({
        ...this.form,
        totalMonthlyExpenditure: this.totalMonthlyExpenditure,
        totalSpendableIncome: this.totalSpendableIncome,
      });

      this.$router.push("/");
    },
  },
  mounted() {
    if (this.quoteResponse?.Answers)
      populateFactFindFromApi(this.quoteResponse.Answers, this.form);
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-text-field.v-text-field--enclosed .v-text-field__details) {
  padding-left: 0;
}

.input-disabled :deep(.v-input--is-disabled),
.input-disabled :deep(input) {
  color: rgba(0, 0, 0, 0.87) !important;
}

.input-disabled :deep(fieldset) {
  color: rgba(0, 0, 0, 0.38) !important;
}

.input-disabled :deep(.v-input__slot),
.input-disabled :deep(.v-input__slot *) {
  cursor: not-allowed !important;
}

.input-sm {
  width: 100%;
  max-width: 254px;
}
.input-md {
  width: 100%;
  max-width: 442px;
}
.input-lg {
  width: 100%;
  max-width: 542px;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .input-sm,
  .input-md,
  .input-lg {
    max-width: initial;
  }
}
</style>
